import { z } from 'zod';
import { usStates } from '@servicetitan/financing-utils-shared';
import { useMemo } from 'react';

export const createPrequalsFinancingInformationSchema = (
    prequalAmount: number,
    minAmount?: number,
    requireState?: boolean,
    availableStates?: string[],
    skipMaxValidation?: boolean,
) => {
    // Build amount schema step by step
    const baseAmountSchema = z
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .number({ required_error: 'Amount is required', invalid_type_error: '' })
        .positive({ message: 'Amount must be greater than 0' })
        .refine(
            value => {
                if (minAmount != null) {
                    return value >= minAmount;
                }
                return true;
            },
            {
                message:
                    minAmount != null
                        ? `Amount must be at least $${minAmount.toLocaleString()}`
                        : 'Amount is invalid',
            },
        );

    // Skip max validation for GoodLeap or when explicitly requested
    const amountSchema =
        prequalAmount > 0 && !skipMaxValidation
            ? baseAmountSchema.refine(value => value <= prequalAmount, {
                  message: `Amount cannot exceed $${prequalAmount.toLocaleString()}`,
              })
            : baseAmountSchema;

    const baseSchema = z.object({
        amount: amountSchema,
    });

    if (requireState) {
        // Use availableStates if provided (from rule), otherwise fall back to all US states
        const statesToValidate =
            availableStates && availableStates.length > 0 ? availableStates : usStates;
        return baseSchema.extend({
            state: z
                // eslint-disable-next-line @typescript-eslint/naming-convention
                .string({ required_error: 'State is required' })
                .min(1, { message: 'State is required' })
                .refine(value => statesToValidate.includes(value), {
                    message: 'Please select a valid state',
                }),
        });
    }

    return baseSchema;
};

export type PrequalsFinancingInformationFormData = z.infer<
    ReturnType<typeof createPrequalsFinancingInformationSchema>
>;

/**
 * Hook to create and memoize the prequalification financing information schema
 */
export const usePrequalsFinancingInformationSchema = (
    prequalAmount: number,
    minAmount: number | undefined,
    requireState: boolean,
    availableStates: string[],
    skipMaxValidation?: boolean,
) => {
    return useMemo(
        () =>
            createPrequalsFinancingInformationSchema(
                prequalAmount,
                minAmount,
                requireState,
                availableStates,
                skipMaxValidation,
            ),
        [prequalAmount, minAmount, requireState, availableStates, skipMaxValidation],
    );
};
